import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Play, Pause, Volume2, Search, Plus, Star, Radio as RadioIcon, X } from 'lucide-react';
import { radio } from '../radioPlayer';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/boards/data/radio`;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

export default function RadioWidget({ config, onConfig, size }) {
  const [st, setSt] = useState(radio.get());
  const [presets, setPresets] = useState([]);
  const [tab, setTab] = useState('presets');
  const [q, setQ] = useState('');
  const [results, setResults] = useState([]);
  const [custom, setCustom] = useState({ name: '', url: '' });
  const favs = config?.favorites || [];

  useEffect(() => radio.subscribe(setSt), []);
  useEffect(() => { axios.get(`${API}/presets`, auth()).then(r => setPresets(r.data.stations || [])).catch(() => {}); }, []);
  useEffect(() => {
    if (tab !== 'search' || q.trim().length < 2) return;
    const t = setTimeout(() => axios.get(`${API}/search`, { ...auth(), params: { q } }).then(r => setResults(r.data.stations || [])).catch(() => setResults([])), 300);
    return () => clearTimeout(t);
  }, [q, tab]);

  const isCur = (s) => st.station && st.station.url === s.url;
  const toggleFav = (s) => {
    const f = { name: s.name || s.label, url: s.url, favicon: s.favicon || '' };
    onConfig({ favorites: favs.some(x => x.url === f.url) ? favs.filter(x => x.url !== f.url) : [...favs, f] });
  };
  const addCustom = () => {
    if (!custom.url.trim()) return;
    const s = { name: custom.name.trim() || custom.url.trim(), url: custom.url.trim() };
    onConfig({ favorites: [...favs, s] });
    radio.play(s);
    setCustom({ name: '', url: '' });
  };

  const list = tab === 'presets' ? presets : tab === 'favs' ? favs : results;
  const tall = size.h >= 5;
  return (
    <div className="h-full flex flex-col text-slate-800" data-testid="radio-widget">
      {/* now playing */}
      <div className="px-3 pt-3 pb-2 flex items-center gap-3 border-b border-slate-200">
        <button onClick={() => (st.playing || st.loading) ? radio.stop() : st.station && radio.play(st.station)} disabled={!st.station}
          className={`no-drag h-11 w-11 rounded-full flex items-center justify-center shrink-0 shadow ${st.playing ? 'bg-blue-600 text-white' : 'bg-slate-900 text-white disabled:bg-slate-300'}`} data-testid="radio-toggle">
          {st.playing || st.loading ? <Pause className="h-5 w-5" /> : <Play className="h-5 w-5 ml-0.5" />}
        </button>
        <div className="min-w-0 flex-1">
          <p className="text-[10px] uppercase tracking-wide text-slate-500">{st.loading ? 'Connecting…' : st.playing ? 'Now playing' : st.station ? 'Paused' : 'Pick a station'}</p>
          <p className="text-sm font-bold truncate" data-testid="radio-now">{st.station?.name || '—'}</p>
          {st.error && <p className="text-[11px] text-red-600 truncate" data-testid="radio-error">{st.error}</p>}
        </div>
        {st.playing && <Eq />}
        <div className="flex items-center gap-1 no-drag" title="Volume">
          <Volume2 className="h-3.5 w-3.5 text-slate-500" />
          <input type="range" min="0" max="1" step="0.02" value={st.volume} onChange={e => radio.setVolume(Number(e.target.value))} className="w-16 accent-blue-600" data-testid="radio-volume" />
        </div>
      </div>
      {tall && (
        <>
          <div className="px-3 pt-2 flex items-center gap-1 text-xs no-drag">
            {[['presets', 'Stations'], ['favs', `Mine (${favs.length})`], ['search', 'Search'], ['url', 'Add URL']].map(([k, l]) => (
              <button key={k} onClick={() => setTab(k)} className={`px-2 py-1 rounded-md font-semibold ${tab === k ? 'bg-slate-900 text-white' : 'text-slate-600 hover:bg-slate-100'}`} data-testid={`radio-tab-${k}`}>{l}</button>
            ))}
          </div>
          {tab === 'search' && (
            <div className="px-3 pt-2 relative no-drag">
              <Search className="h-3.5 w-3.5 absolute left-5 top-4 text-slate-400" />
              <input value={q} onChange={e => setQ(e.target.value)} placeholder="Station name (any country)…" className="w-full pl-8 pr-2 py-1.5 text-sm border border-slate-300 rounded-lg" data-testid="radio-search" />
            </div>
          )}
          {tab === 'url' ? (
            <div className="p-3 space-y-2 no-drag">
              <input value={custom.name} onChange={e => setCustom({ ...custom, name: e.target.value })} placeholder="Name (e.g. Shop playlist)" className="w-full px-2 py-1.5 text-sm border border-slate-300 rounded-lg" data-testid="radio-custom-name" />
              <input value={custom.url} onChange={e => setCustom({ ...custom, url: e.target.value })} placeholder="https://… stream URL (mp3/aac/m3u8)" className="w-full px-2 py-1.5 text-sm border border-slate-300 rounded-lg font-mono" data-testid="radio-custom-url" />
              <button onClick={addCustom} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-blue-600 text-white text-xs font-bold" data-testid="radio-custom-add"><Plus className="h-3.5 w-3.5" />Add & play</button>
            </div>
          ) : (
            <div className="flex-1 min-h-0 overflow-y-auto px-2 pb-2 pt-1 no-drag divide-y divide-slate-100">
              {list.map((s) => (
                <div key={s.uuid || s.url} className={`flex items-center gap-2 px-1.5 py-1.5 rounded-lg ${isCur(s) ? 'bg-blue-50' : 'hover:bg-slate-50'}`}>
                  <button onClick={() => isCur(s) && st.playing ? radio.stop() : radio.play(s)} className="h-7 w-7 rounded-full bg-slate-100 hover:bg-slate-200 flex items-center justify-center shrink-0" data-testid={`radio-play-${(s.uuid || s.url).slice(-8)}`}>
                    {isCur(s) && st.playing ? <Pause className="h-3.5 w-3.5" /> : <Play className="h-3.5 w-3.5 ml-0.5" />}
                  </button>
                  {s.favicon ? <img src={s.favicon} alt="" className="h-6 w-6 rounded object-cover bg-white border border-slate-200" onError={e => { e.currentTarget.style.visibility = 'hidden'; }} /> : <RadioIcon className="h-5 w-5 text-slate-400" />}
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold truncate">{s.label || s.name}</p>
                    <p className="text-[10px] text-slate-500 truncate">{s.genre || s.tags || s.state || ''}{s.bitrate ? ` · ${s.bitrate} kbps` : ''}</p>
                  </div>
                  <button onClick={() => toggleFav(s)} title="Save to mine" className="p-1 text-slate-400 hover:text-amber-500" data-testid={`radio-fav-${(s.uuid || s.url).slice(-8)}`}>
                    {favs.some(x => x.url === s.url) ? <Star className="h-4 w-4 fill-amber-400 text-amber-400" /> : tab === 'favs' ? <X className="h-4 w-4" /> : <Star className="h-4 w-4" />}
                  </button>
                </div>
              ))}
              {list.length === 0 && <p className="text-xs text-slate-400 p-3">{tab === 'search' ? 'Type a station name to search 50,000+ streams.' : tab === 'favs' ? 'Star stations to keep them here.' : 'Loading stations…'}</p>}
            </div>
          )}
        </>
      )}
    </div>
  );
}

function Eq() {
  return (
    <div className="flex items-end gap-0.5 h-5" aria-hidden>
      {[0, 1, 2, 3].map(i => <span key={i} className="w-1 bg-blue-600 rounded-sm animate-eq" style={{ animationDelay: `${i * 0.15}s` }} />)}
    </div>
  );
}
